import {HDate, months} from '@hebcal/hdate';
import {CalOptions} from './CalOptions.js';
import {Location} from './location.js';
import {Event, flags} from './event.js';
import {ChanukahEvent, HolidayEvent} from './HolidayEvent.js';
import {Zmanim} from './zmanim.js';
import {TimedEvent, CandleLightingEvent, HavdalahEvent} from './TimedEvent.js';
import {holidayDesc as hdesc} from './staticHolidays.js';
import {DEG_6_POINT_45, DEG_7_POINT_083, DEG_16_POINT_1} from './zenith.js';

const {LIGHT_CANDLES, LIGHT_CANDLES_TZEIS} = flags;
const {FAST_BEGINS, FAST_ENDS} = hdesc;

/** @internal */
export function makeCandleEvent(
  ev: Event | undefined,
  hd: HDate,
  options: CalOptions,
  isFriday: boolean,
  isSaturday: boolean
): TimedEvent | undefined {
  let havdalahTitle = false;
  let useHavdalahOffset = isSaturday;
  let mask = ev ? ev.mask : LIGHT_CANDLES;
  if (ev !== undefined) {
    // if linked event && dow == FRI, use Candle lighting time & title
    if (!isFriday) {
      if (ev.hasAnyFlag('LIGHT_CANDLES_TZEIS', 'CHANUKAH_CANDLES')) {
        useHavdalahOffset = true;
      } else if (ev.hasFlag('YOM_TOV_ENDS')) {
        havdalahTitle = true;
        useHavdalahOffset = true;
      }
    }
  } else if (isSaturday) {
    havdalahTitle = true;
    mask = LIGHT_CANDLES_TZEIS;
  }
  // if Havdalah offset is 0 or undefined, we'll use tzeit time
  const offset = useHavdalahOffset
    ? Number(options.havdalahMins)
    : Number(options.candleLightingMins);
  const location = options.location as Location;
  const useElevation = Boolean(options.useElevation);
  const zmanim = new Zmanim(location, hd, useElevation);
  const time =
    useHavdalahOffset && !offset
      ? zmanim.tzeit(options.havdalahDeg)
      : zmanim.sunsetOffset(offset, true);
  if (isNaN(time.getTime())) {
    return undefined; // no sunset
  }
  if (havdalahTitle) {
    return new HavdalahEvent(
      hd,
      mask,
      time,
      location,
      options.havdalahMins,
      ev,
      options
    );
  } else {
    mask |= LIGHT_CANDLES;
    return new CandleLightingEvent(hd, mask, time, location, ev, options);
  }
}

/**
 * Minutes after sunset that minor fasts (including Yom Kippur Katan) end in
 * Israel by default, following Rabbi Deblitzky's practice.
 * @see {https://www.yeshiva.org.il/calendar/timeprinciples}
 */
const MINOR_FAST_END_MINUTES_IL = 15;

/**
 * Returns `val` when it is a finite nonzero number, otherwise `undefined`
 */
function nonzero(val: number | undefined): number | undefined {
  return typeof val === 'number' && isFinite(val) && val !== 0 ? val : undefined;
}

/**
 * Computes the "Fast begins" time for a minor fast (one that begins at dawn).
 *
 * When `options.fastStartMins` is a nonzero number, the fast begins that many
 * minutes before sunrise; otherwise, when `options.fastStartDeg` is a nonzero
 * number, the fast begins when the sun is that many degrees below the horizon
 * in the morning. By default, minor fasts begin at Alot HaShachar (16.1°).
 */
function makeFastStartTime(zmanim: Zmanim, options: CalOptions): Date {
  const fastStartMins = nonzero(options.fastStartMins);
  if (fastStartMins !== undefined) {
    return zmanim.sunriseOffset(-Math.abs(fastStartMins), true);
  }
  const fastStartDeg = nonzero(options.fastStartDeg);
  return zmanim.timeAtAngle(
    fastStartDeg === undefined ? DEG_16_POINT_1 : Math.abs(fastStartDeg),
    true
  );
}

/**
 * Computes the "Fast ends" time for a fast day.
 *
 * Tish'a B'Av ends `options.tishaBavEndMins` minutes after sunset or at tzeit
 * `options.tishaBavEndDeg` when either is a nonzero number, and otherwise at
 * tzeit 6.45° (Rabbi Yechiel Michel Tucazinsky). It is not affected by
 * `options.fastEndDeg`, `options.fastEndMins` or `options.il`.
 *
 * For minor fasts: when `options.fastEndMins` is a nonzero number, the fast
 * ends that many minutes after sunset; otherwise, when `options.fastEndDeg` is
 * a nonzero number, the fast ends at tzeit for that solar depression angle.
 * When neither is specified, the default depends on `options.il`:
 * - In Israel, minor fasts end 15 minutes after sunset (Rabbi Deblitzky's practice).
 * - Elsewhere, minor fasts end at tzeit 7.083° (3 medium-sized stars).
 */
function makeFastEndTime(
  zmanim: Zmanim,
  isTishaBav: boolean,
  options: CalOptions
): Date {
  if (isTishaBav) {
    const tishaBavEndMins = nonzero(options.tishaBavEndMins);
    if (tishaBavEndMins !== undefined) {
      return zmanim.sunsetOffset(Math.abs(tishaBavEndMins), true);
    }
    const tishaBavEndDeg = nonzero(options.tishaBavEndDeg);
    return zmanim.tzeit(
      tishaBavEndDeg === undefined ? DEG_6_POINT_45 : Math.abs(tishaBavEndDeg)
    );
  }
  const fastEndMins = options.fastEndMins;
  if (typeof fastEndMins === 'number' && fastEndMins !== 0) {
    return zmanim.sunsetOffset(Math.abs(fastEndMins), true);
  }
  const fastEndDeg = options.fastEndDeg;
  if (typeof fastEndDeg === 'number' && fastEndDeg !== 0) {
    return zmanim.tzeit(Math.abs(fastEndDeg));
  }
  if (options.il) {
    return zmanim.sunsetOffset(MINOR_FAST_END_MINUTES_IL, true);
  }
  return zmanim.tzeit(DEG_7_POINT_083);
}

/**
 * A fast day holiday with attached start and end time events.
 *
 * Wraps an underlying fast-day {@link HolidayEvent} and (when computable
 * for the given location) exposes a "Fast begins" and "Fast ends"
 * {@link TimedEvent}. Generated by {@link calendar} when
 * `options.candlelighting` is `true` and `options.location` is provided.
 *
 * - Minor fasts (including Yom Kippur Katan) begin at *Alot HaShachar*
 *   (16.1° below horizon in the morning), which can be overridden via
 *   `options.fastStartDeg` or `options.fastStartMins`. By default they end at
 *   tzeit 7.083° (3 medium-sized stars) in the Diaspora, or 15 minutes after
 *   sunset in Israel (Rabbi Deblitzky's practice). This can be overridden
 *   via `options.fastEndDeg` or `options.fastEndMins`.
 * - Tish'a B'Av begins at sunset on the previous day, regardless of
 *   `options.fastStartDeg` / `options.fastStartMins`. By default it ends at
 *   tzeit 6.45° below horizon (Rabbi Yechiel Michel Tucazinsky), which can be
 *   overridden via `options.tishaBavEndDeg` or `options.tishaBavEndMins`
 *   (but not by `options.fastEndDeg` / `options.fastEndMins`).
 * - When a minor fast falls on a Friday, the end time is suppressed
 *   (Shabbat begins before nightfall).
 */
export class FastDayEvent extends HolidayEvent {
  /** original event */
  readonly linkedEvent: HolidayEvent;
  /** this will be a "Fast begins" event */
  readonly startEvent?: TimedEvent;
  /** this will be a "Fast ends" event */
  readonly endEvent?: TimedEvent;
  constructor(
    linkedEvent: HolidayEvent,
    startEvent?: TimedEvent,
    endEvent?: TimedEvent
  ) {
    super(linkedEvent.getDate(), linkedEvent.getDesc(), linkedEvent.mask);
    this.linkedEvent = linkedEvent;
    this.startEvent = startEvent;
    this.endEvent = endEvent;
  }
  render(locale?: string): string {
    return this.linkedEvent.render(locale);
  }
  renderBrief(locale?: string): string {
    return this.linkedEvent.renderBrief(locale);
  }
  urlDateSuffix(): string {
    return this.linkedEvent.urlDateSuffix();
  }
  url(): string | undefined {
    return this.linkedEvent.url();
  }
  getEmoji(): string {
    return this.linkedEvent.getEmoji();
  }
  getCategories(): string[] {
    return this.linkedEvent.getCategories();
  }
}

/**
 * Makes a pair of events representing fast start and end times
 * @internal
 */
export function makeFastStartEnd(
  ev: HolidayEvent,
  options: CalOptions
): FastDayEvent {
  const desc = ev.getDesc();
  if (desc === 'Yom Kippur') {
    throw new RangeError('YK does not require this function');
  }
  const hd = ev.getDate();
  const dt = hd.greg();
  const location = options.location as Location;
  const useElevation = Boolean(options.useElevation);
  const zmanim = new Zmanim(location, dt, useElevation);
  let startEvent: TimedEvent | undefined;
  let endEvent: TimedEvent | undefined;
  if (desc === "Erev Tish'a B'Av") {
    const sunset = zmanim.sunset();
    if (!isNaN(sunset.getTime())) {
      startEvent = makeTimedEvent(ev, sunset, FAST_BEGINS, options);
    }
  } else if (desc.startsWith("Tish'a B'Av")) {
    const fastEnd = makeFastEndTime(zmanim, true, options);
    if (!isNaN(fastEnd.getTime())) {
      endEvent = makeTimedEvent(ev, fastEnd, FAST_ENDS, options);
    }
  } else {
    const fastStart = makeFastStartTime(zmanim, options);
    if (!isNaN(fastStart.getTime())) {
      startEvent = makeTimedEvent(ev, fastStart, FAST_BEGINS, options);
    }
    if (
      dt.getDay() !== 5 &&
      !(hd.getDate() === 14 && hd.getMonth() === months.NISAN)
    ) {
      const fastEnd = makeFastEndTime(zmanim, false, options);
      if (!isNaN(fastEnd.getTime())) {
        endEvent = makeTimedEvent(ev, fastEnd, FAST_ENDS, options);
      }
    }
  }
  const ev2 = new FastDayEvent(ev, startEvent, endEvent);
  // copy properties such as memo or emoji
  Object.assign(ev2, ev);
  return ev2;
}

function makeTimedEvent(
  ev: Event,
  time: Date,
  desc: string,
  options: CalOptions
): TimedEvent {
  const location = options.location as Location;
  const hd = ev.getDate();
  return new TimedEvent(hd, desc, ev.mask, time, location, ev, options);
}

/**
 * A Chanukah candle-lighting event that carries a specific time of day.
 *
 * Generated by {@link calendar} when both `options.candlelighting`
 * and `options.location` are set. On weekdays the time is Bein HaShmashos
 * (13.5 minutes before the sun is 7.083° below the horizon in the evening);
 * on Friday it is regular candle-lighting time, and on Saturday night it is
 * regular Havdalah time.
 */
export class TimedChanukahEvent extends ChanukahEvent {
  /** Time at which candles are lit, rounded to the nearest minute */
  eventTime: Date;
  /** {@link eventTime} formatted for the event's location, e.g. `'4:31pm'` */
  eventTimeStr: string;
  /** Location used to calculate {@link eventTime} */
  readonly location: Location;
  /**
   * Normally created by {@link calendar} rather than directly.
   * @param ev the untimed Chanukah event this is derived from
   * @param eventTime time at which candles are lit
   * @param location location used to format the time
   */
  constructor(ev: ChanukahEvent, eventTime: Date, location: Location) {
    super(ev.getDate(), ev.getDesc(), ev.mask, ev.chanukahDay);
    this.eventTime = Zmanim.roundTime(eventTime);
    const timeFormat = location.getTimeFormatter();
    this.eventTimeStr = Zmanim.formatTime(this.eventTime, timeFormat);
    this.location = location;
    this.emoji = ev.emoji;
  }
}

/**
 * Makes a candle-lighting event for Chankah (not on Friday/Saturday).
 * At one point this used civil dusk (6 degrees below horizon).
 * Another source suggests 4.6667 degrees below horizon.
 * @internal
 */
export function makeWeekdayChanukahCandleLighting(
  ev: ChanukahEvent,
  options: CalOptions
): TimedChanukahEvent | null {
  const hd = ev.getDate();
  const location = options.location as Location;
  const useElevation = Boolean(options.useElevation);
  const zmanim = new Zmanim(location, hd.greg(), useElevation);
  const candleLightingTime = zmanim.beinHaShmashos();
  if (isNaN(candleLightingTime.getTime())) {
    return null;
  }
  return new TimedChanukahEvent(ev, candleLightingTime, location);
}
